<template>
  <div>
    <!-- Filters -->

    <app-collapse hover>
      <app-collapse-item title="Filters">
        <holiday-list-filters
          :filter-by-type.sync="filterByType"
          :type-option="typeOption"
          :filter-by-shift.sync="filterByShift"
          :shift-option="shiftOption"
        />
      </app-collapse-item>
    </app-collapse>

    <!-- Table Container Card -->
    <b-card no-body class="mb-0 mt-2">
      <div class="m-1">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-checkbox
              v-if="holidaysMetaData.length && $can('holiday_delete')"
              class="mr-2"
              v-model="isDeleteMultipleCandChecked"
            >
            </b-form-checkbox>
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              :searchable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div
              v-if="selectedHolidays.length == 0"
              class="d-flex align-items-center justify-content-end"
            >
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  placeholder="Search Holiday / Type / date :YYYY-MM-DD"
                />
              </b-input-group>
              <b-button
                v-if="$can('holiday_create')"
                variant="primary"
                :to="{ name: 'add-holiday' }"
              >
                <span class="text-nowrap">Add Holiday</span>
              </b-button>
            </div>
            <b-button
              v-else-if="$can('holiday_delete')"
              class="float-right"
              variant="danger"
              :disabled="holidaysDeleteCount == 0"
              @click="confirmDeleteMultipleRecords"
            >
              <span class="text-nowrap"
                >Delete {{ holidaysDeleteCount }} Holiday</span
              >
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-form-checkbox-group
        id="checkbox-group"
        v-model="selectedHolidays"
        name="selectedHolidays"
      >
        <b-table
          ref="refUserListTable"
          class="position-relative"
          :items="fetchHolidayList"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(select)="data">
            <div class="text-nowrap">
              <b-form-checkbox :value="data.item.hashid"> </b-form-checkbox>
            </div>
          </template>
          <!-- Column: User -->
          <template #cell(holiday_name)="data">
            <b-media vertical-align="center">
              <b-link
                v-if="$can('holiday_show')"
                :to="{
                  name: 'view-holiday',
                  params: { id: data.item.hashid },
                }"
                class="font-weight-bold d-inline-block text-nowrap"
                v-b-tooltip.hover.v-default
                :title="data.item.holiday_name"
              >
                {{ data.item.holiday_name | str_limit(15) }}
              </b-link>
              <span
                v-else
                v-b-tooltip.hover.v-default
                :title="data.item.holiday_name"
              >
                {{ data.item.holiday_name | str_limit(15) }}
              </span>
            </b-media>
          </template>

          <template #cell(date)="data">
            {{ data.item.date | formatDate }}
          </template>

          <template #cell(shifts)="data">
            <span
              v-b-tooltip.hover.v-default
              :title="data.item.shifts | formatArrayToSting('name')"
            >
              {{
                data.item.shifts | formatArrayToSting("name") | str_limit(20)
              }}
            </span>
          </template>

          <!--Column: holiday type -->
          <template #cell(holiday_type)="data">
            <b-badge
              pill
              :variant="`light-${resolveHolidayShiftVariant(
                data.item.holiday_type
              )}`"
              class="text-capitalize"
            >
              {{ data.item.holiday_type }}
            </b-badge>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">

             <feather-icon
                @click="editRecord(data)"
                v-if="$can('holiday_edit')"
                v-b-tooltip.hover
                title="Edit"
                icon="EditIcon"
                size="16"
                class="mr-1 cursor-pointer"
              />
              <feather-icon
                @click="viewRecord(data)"
                v-if="$can('holiday_show')"
                v-b-tooltip.hover
                title="View"
                icon="EyeIcon"
                size="16"
                class="cursor-pointer"
              />
            <b-dropdown
              variant="link"
              size="sm"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>

              <b-dropdown-item
                class="btn-sm p-0"
                @click="confirmDeleteRecord(data)"
                v-if="$can('holiday_delete')"
              >
                <feather-icon icon="TrashIcon" size="13" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-form-checkbox-group>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="
              d-flex
              align-items-center
              justify-content-center justify-content-sm-start
            "
          >
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
              {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="
              d-flex
              align-items-center
              justify-content-center justify-content-sm-end
            "
          >
            <b-pagination
              v-if="selectedHolidays.length == 0"
              v-model="currentPage"
              :total-rows="totalHolidayList"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
            <b-button
              v-else-if="$can('holiday_delete')"
              class="float-right"
              variant="danger"
              :disabled="holidaysDeleteCount == 0"
              @click="confirmDeleteMultipleRecords"
            >
              <span class="text-nowrap"
                >Delete {{ holidaysDeleteCount }} Holiday</span
              >
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  VBTooltip,
  BFormCheckbox,
  BFormCheckboxGroup,
} from "bootstrap-vue";
import vSelect from "vue-select";
import store from "@/store";
import { onUnmounted, watch, ref } from "@vue/composition-api";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import holidayStoreModule from "../holidayStoreModule";
import useHolidayList from "./useHolidayList";
import HolidayListFilters from "./HolidayListFilters.vue";

export default {
  components: {
    HolidayListFilters,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BPagination,
    AppCollapse,
    AppCollapseItem,
    vSelect,
    /* eslint-disable */
    ToastificationContent,
    /* eslint-enable */
    BFormCheckbox,
    BFormCheckboxGroup,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  setup() {
    const REFERRAL_APP_STORE_MODULE_NAME = "app-holidays";
    const isDeleteMultipleCandChecked = ref(false);
    const selectedHolidays = ref([]);
    const holidaysDeleteCount = ref(0);

    // Register module
    if (!store.hasModule(REFERRAL_APP_STORE_MODULE_NAME)) {
      store.registerModule(REFERRAL_APP_STORE_MODULE_NAME, holidayStoreModule);
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(REFERRAL_APP_STORE_MODULE_NAME))
        store.unregisterModule(REFERRAL_APP_STORE_MODULE_NAME);
    });

    const statusOptions = [
      { label: "In Progress", code: 0 },
      { label: "Selected", code: 1 },
      { label: "Rejected", code: 2 },
    ];
    const {
      fetchHolidayList,
      tableColumns,
      perPage,
      currentPage,
      totalHolidayList,
      holidaysMetaData,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      filterByType,
      typeOption,
      filterByShift,
      shiftOption,
      resolveHolidayShiftVariant,
    } = useHolidayList();

    watch(isDeleteMultipleCandChecked, (val) => {
      selectedHolidays.value = [];
      if (val) {
        holidaysMetaData.value.forEach((holiday) => {
          selectedHolidays.value.push(holiday.hashid);
        });
      }
    });

    watch(selectedHolidays, (val) => {
      holidaysDeleteCount.value = val.length;
    });

    return {
      // Sidebar
      fetchHolidayList,
      tableColumns,
      perPage,
      currentPage,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      totalHolidayList,
      // Extra Filters
      selectedHolidays,
      holidaysDeleteCount,
      isDeleteMultipleCandChecked,
      filterByType,
      typeOption,
      filterByShift,
      shiftOption,
      resolveHolidayShiftVariant,
      holidaysMetaData,
    };
  },
  data() {
    return {};
  },
  created() {
    if (!this.$can("holiday_delete")) {
      this.tableColumns = this.tableColumns.filter(
        (item) => item.key != "select"
      );
    }
  },
  methods: {
    editRecord(data) {
      this.$router.push(`/edit/holiday/${data.item.hashid}`).catch(() => {});
    },
    viewRecord(data) {
      this.$router.push(`/view/holiday/${data.item.hashid}`).catch(() => {});
    },
    confirmDeleteRecord(data) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${data.item.holiday_name}.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.deleteRecord(data.item.hashid);
        }
      });
    },
    confirmDeleteMultipleRecords() {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${this.selectedHolidays.length} Holiday, This cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch(
              "app-holidays/removeMultipleHolidays",
              this.selectedHolidays
            )
            .then((res) => {
              this.isDeleteMultipleCandChecked = false;
              this.selectedHolidays = [];
              if (res.data.success) {
                this.refetchData();
                // this.$toast({
                //   component: ToastificationContent,
                //   props: {
                //     title: "Deleted Successfully.",
                //     icon: "BellIcon",
                //     variant: "success",
                //     text: res.data.message,
                //   },
                // });
                this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Deleted Successfully.',
                icon: 'BellIcon',
                variant: 'success',
                text: res.data.message,
              },
            }, {
              timeout: 2000, 
              position: 'bottom-center',
              toastClassName:"radius-8 successful-info",
              hideProgressBar : true,
            });
              } else {
                // this.$toast({
                //   component: ToastificationContent,
                //   props: {
                //     title: "Holiday Deletion failed.",
                //     icon: "BellIcon",
                //     variant: "danger",
                //     text: res.data.message,
                //   },
                // });
                this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Holiday Deletion failed.',
                icon: 'BellIcon',
                variant: 'danger',
                text: res.data.message,
              },
            }, {
              timeout: 2000, 
              position: 'bottom-center',
              toastClassName:"radius-8 error-info ",
              hideProgressBar : true,
            });
              }
            })
            .catch((err) => console.log(err));
        }
      });
    },
    refetchHolidaysData() {
      console.log("Added Holiday");
    },
    deleteRecord(id) {
      const self = this;
      this.$store
        .dispatch("app-holidays/removeHoliday", id)
        .then((res) => {
          if (res.status == "204") {
            // self.$toast({
            //   component: ToastificationContent,
            //   props: {
            //     title: "Holiday Deleted",
            //     icon: "BellIcon",
            //     variant: "success",
            //     text: "Holiday Deleted Successfully.",
            //   },
            // });
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Holiday Deleted',
                icon: 'BellIcon',
                variant: 'success',
                text: 'Holiday Deleted Successfully.',
              },
            }, {
              timeout: 2000, 
              position: 'bottom-center',
              toastClassName:"radius-8 successful-info",
              hideProgressBar : true,
            });
          } else {
            // self.$toast({
            //   component: ToastificationContent,
            //   props: {
            //     title: "Holiday Deletion failed.",
            //     icon: "BellIcon",
            //     variant: "danger",
            //     text: res.data.message,
            //   },
            // });
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Holiday Deletion failed.',
                icon: 'BellIcon',
                variant: 'danger',
                text: res.data.message,
              },
            }, {
              timeout: 2000, 
              position: 'bottom-center',
              toastClassName:"radius-8 error-info ",
              hideProgressBar : true,
            });
          }
          self.refetchData();
        })
        .catch((err) => {
          // self.$toast({
          //   component: ToastificationContent,
          //   props: {
          //     title: "Holiday Deletion failed.",
          //     icon: "BellIcon",
          //     variant: "danger",
          //   },
          // });
          this.$toast({
              component: ToastificationContent,
              props: {
                title: `Holiday Deletion failed.`,
                icon:'BellIcon',
                variant : 'danger',
                hideClose: true,
                
              },
                  },
                  {
              timeout : 3000,
              position : 'bottom-center',
              toastClassName:"error-info",
              hideProgressBar : true,
                  })
          console.error(err);
        });
    },
    openWindow(link) {
      window.open(link);
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
